import { ApolloError } from '@apollo/client'
import { useCallback, useEffect, useState } from 'react'
import {
    CommentThreadItemFragment,
    useGetCommentThreadQuery,
    useRefreshCommentThreadMutation
} from '../../api/client'
import { graphqlErrorMessage } from '../../util/graphqlErrorMessage'

export type ConversationState = 'loading' | 'fetching' | 'ready' | 'parent-unavailable' | 'error'

/**
 * Loads the thread for the drawer and, when the root's parent is missing and
 * the server says it can fetch (platform supported, cooldown clear), asks the
 * platform once per comment. Anything else the platform might add (new
 * replies, a parent for an old reply) waits for the moderator to click Fetch
 * latest: every open fetching would spend a platform call per comment viewed.
 * The result of a refresh replaces the thread, so no second query is needed.
 */
export function useCommentThread(contentId: number | null) {
    const { data, loading, error, refetch } = useGetCommentThreadQuery({
        variables: { contentId: contentId ?? 0 },
        skip: contentId === null,
        fetchPolicy: 'network-only'
    })
    const [refreshMutation, { loading: refreshing }] = useRefreshCommentThreadMutation()
    // Both pieces of state remember which comment they belong to. A refresh
    // can resolve after the user has moved to another comment; keyed state
    // means that late result is simply ignored for the new comment, instead of
    // being shown as its thread, so moving between comments needs no reset.
    const [refreshed, setRefreshed] = useState<
        { contentId: number; thread: CommentThreadItemFragment } | undefined
    >()
    const [triedAutoFor, setTriedAutoFor] = useState<number | null>(null)
    // Why the last refresh failed, in the server's plain English. Keyed like
    // the refreshed thread, so it never shows against another comment.
    const [failure, setFailure] = useState<{ contentId: number; message: string } | undefined>()

    // The drawer stays mounted in ContentList while closed, so this state
    // would otherwise outlive it: reopening the same comment would show the
    // old refreshed thread over the fresh network-only read, and never fetch
    // from the platform again. Closing the drawer forgets all of it.
    useEffect(() => {
        if (contentId === null) {
            setRefreshed(undefined)
            setTriedAutoFor(null)
            setFailure(undefined)
        }
    }, [contentId])

    const thread = refreshed?.contentId === contentId ? refreshed.thread : data?.getCommentThread

    const refresh = useCallback(async () => {
        if (contentId === null) return
        const id = contentId
        setFailure(undefined)
        try {
            const result = await refreshMutation({ variables: { contentId: id } })
            if (result.data?.refreshCommentThread) {
                setRefreshed({ contentId: id, thread: result.data.refreshCommentThread })
            }
        } catch (err) {
            console.error('Error refreshing conversation thread:', err)
            setFailure({
                contentId: id,
                message: graphqlErrorMessage(
                    err,
                    "Couldn't check for new replies. Showing what Arwen holds."
                )
            })
            // Drop any earlier refreshed thread for this comment so the
            // recovery read is what the drawer shows.
            setRefreshed((current) => (current?.contentId === id ? undefined : current))
            await refetch()
        }
    }, [contentId, refreshMutation, refetch])

    useEffect(() => {
        if (
            contentId !== null &&
            triedAutoFor !== contentId &&
            thread?.parentMissing &&
            thread.canRefresh
        ) {
            setTriedAutoFor(contentId)
            void refresh()
        }
    }, [contentId, thread, triedAutoFor, refresh])

    // Fetching wins over the read's own states: while the platform is being
    // asked, the drawer shows what it holds plus the fetching placeholder.
    let state: ConversationState = 'ready'
    if (refreshing) state = 'fetching'
    else if (error) state = 'error'
    else if (loading && !thread) state = 'loading'
    else if (thread?.parentMissing) state = 'parent-unavailable'

    const refreshError = failure?.contentId === contentId ? failure.message : null
    const notices = thread?.notices ?? []

    return {
        thread,
        state,
        refresh,
        refreshing,
        refreshError,
        notices,
        error: error as ApolloError | undefined
    }
}
